<script setup lang="ts">
import type { CheckboxTileRegistration, CheckboxTileValue, VCheckboxTileGroupProps } from "./VCheckboxTile.types";
import { computed, provide, ref, shallowRef, useId } from "vue";
import { useI18n } from "vue-i18n";
import { choiceTileLayoutClass } from "../ChoiceTile/choiceTileLayout";
import { formErrorMessageCva } from "../Form/form.cva";
import VFieldLabel from "../Form/VFieldLabel.vue";
import { CHECKBOX_TILE_GROUP_KEY } from "./VCheckboxTile.types";

defineOptions({
  name: "VCheckboxTileGroup",
});

/**
 * Group of checkbox tiles: the look of VRadioTileGroup (illustration, title, subtitle, `row` or `stacked`), but every
 * tile is a native checkbox, so the user can choose several. The model holds the checked values in the order of the
 * tiles. Every tile is its own tab stop like any checkbox group; the arrow keys, Home and End also move focus between
 * the tiles without changing them, and Space toggles the focused tile.
 */
const {
  label = undefined,
  help = undefined,
  ariaLabel = undefined,
  ariaLabelledby = undefined,
  name = undefined,
  layout = "stack",
  columns = 2,
  variant = "row",
  disabled = false,
  required = false,
  min = 0,
  errorMessage = undefined,
  showMinError = true,
  testId = undefined,
} = defineProps<VCheckboxTileGroupProps>();

defineSlots<{
  default?: () => unknown
}>();

const modelValue = defineModel<CheckboxTileValue[]>({ default: () => [] });

const { t } = useI18n();
const a11yId = useId();
const labelId = `${a11yId}-label`;
const errorId = `${a11yId}-error`;
const rootRef = ref<HTMLElement | null>(null);
const tiles = shallowRef<CheckboxTileRegistration[]>([]);
// The group's own `min` error waits until the user changed the selection or the form asked for validation.
const touched = ref(false);

const minimum = computed(() => (required ? Math.max(1, min) : Math.max(0, min)));
const minError = computed(() => {
  if (!showMinError || !touched.value || modelValue.value.length >= minimum.value) {
    return null;
  }
  return minimum.value === 1
    ? t("validation.choose_at_least_one")
    : t("validation.choose_at_least_count", { count: minimum.value });
});
const shownError = computed(() => {
  const external = errorMessage?.trim() ?? "";
  return external !== "" ? external : minError.value;
});
const invalid = computed(() => shownError.value !== null);

const layoutClass = computed(() => choiceTileLayoutClass(layout, variant, columns));
const labelledBy = computed(() => (label ? labelId : ariaLabelledby));

/** The registered tiles in document order, so the model follows the order the user sees. */
function tilesInOrder(): CheckboxTileRegistration[] {
  return [...tiles.value].sort((left, right) => {
    const leftElement = left.element();
    const rightElement = right.element();
    if (!leftElement || !rightElement) {
      return 0;
    }
    return leftElement.compareDocumentPosition(rightElement) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });
}

function toggle(value: CheckboxTileValue, checked: boolean) {
  if (disabled) {
    return;
  }
  touched.value = true;
  const current = modelValue.value;
  if (checked === current.includes(value)) {
    return;
  }
  if (!checked) {
    modelValue.value = current.filter(entry => entry !== value);
    return;
  }
  const next = new Set<CheckboxTileValue>([...current, value]);
  const ordered = tilesInOrder().map(tile => tile.value()).filter(entry => next.has(entry));
  // Values without a tile (not rendered right now) stay, after the tiles.
  const unknown = current.filter(entry => !ordered.includes(entry));
  modelValue.value = [...ordered, ...unknown];
}

function register(tile: CheckboxTileRegistration): () => void {
  tiles.value = [...tiles.value, tile];
  return () => {
    tiles.value = tiles.value.filter(entry => entry !== tile);
  };
}

const NEXT_KEYS = new Set(["ArrowRight", "ArrowDown"]);
const PREVIOUS_KEYS = new Set(["ArrowLeft", "ArrowUp"]);

/** Moves focus between the checkboxes that can take focus; a native disabled one is skipped, a locked one is not. */
function onKeydown(event: KeyboardEvent) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || !target.hasAttribute("data-checkbox-tile-input") || !rootRef.value) {
    return;
  }
  const inputs = Array.from(rootRef.value.querySelectorAll<HTMLInputElement>("input[data-checkbox-tile-input]"))
    .filter(input => !input.disabled);
  const position = inputs.indexOf(target);
  if (position < 0 || inputs.length === 0) {
    return;
  }
  let next: HTMLInputElement | undefined;
  if (NEXT_KEYS.has(event.key)) {
    next = inputs[(position + 1) % inputs.length];
  } else if (PREVIOUS_KEYS.has(event.key)) {
    next = inputs[(position - 1 + inputs.length) % inputs.length];
  } else if (event.key === "Home") {
    next = inputs[0];
  } else if (event.key === "End") {
    next = inputs[inputs.length - 1];
  }
  if (!next) {
    return;
  }
  event.preventDefault();
  next.focus();
}

/** Shows the group's own `min` error now, e.g. on submit, and tells whether the selection is valid. */
function validate(): boolean {
  touched.value = true;
  return modelValue.value.length >= minimum.value;
}

/** Hides the group's own `min` error again until the next change, e.g. when the form is reset. */
function resetValidation() {
  touched.value = false;
}

defineExpose({ validate, resetValidation });

provide(CHECKBOX_TILE_GROUP_KEY, {
  name: computed(() => name ?? a11yId),
  modelValue: computed(() => modelValue.value),
  disabled: computed(() => disabled),
  invalid,
  errorId: computed(() => (invalid.value ? errorId : undefined)),
  variant: computed(() => variant),
  register,
  toggle,
});
</script>

<template>
  <div class="flex flex-col" :data-testid="testId">
    <VFieldLabel
      v-if="label"
      :label="label"
      :label-id="labelId"
      :required="required"
      :help="help"
      display="block"
    />
    <div
      ref="rootRef"
      role="group"
      :aria-label="labelledBy ? undefined : ariaLabel"
      :aria-labelledby="labelledBy"
      :aria-describedby="invalid ? errorId : undefined"
      :aria-disabled="disabled || undefined"
      class="@container"
      @keydown="onKeydown"
    >
      <div :class="layoutClass" data-testid="checkbox-tile-group-layout">
        <slot />
      </div>
    </div>
    <span v-if="shownError" :id="errorId" :class="formErrorMessageCva()" data-testid="checkbox-tile-group-error">
      {{ shownError }}
    </span>
  </div>
</template>
